import {
  UserData,
  JUNGLE_BACKEND_DOMAIN_FOR_ROUTES_MIGRATED_TO_CLOUDFLARE_WORKER,
} from "../../constants/GlobalTypesAndEnumsAndConstants";
import { getFirebaseAuthorizationHeaders } from "../utils/firebase-auth-headers";
import { trackError } from "../../utils/analytics/track-error";
import { recordStudyAction } from "../../features/study-materials/studyStartJourney";

// The answers whose streak update the Worker has not answered yet. The first review's streak day is asked for only
// once they are in (recordTheFirstReviewAsTodaysStreakDay), so the Worker counts the day after them.
const streakUpdatesInFlight = new Set<Promise<void>>();
// The page gives the whole operation four seconds. Reserve most of that for
// authentication and recording the day, rather than spending it all on a stalled answer.
const FIRST_REVIEW_ANSWER_SETTLEMENT_WAIT_MILLISECONDS = 1_000;

// We will add 1 user streak to the database after a user answers 10 questions today
export const updateStreakAfterAnsweringAQuestion = async (user: UserData, isCorrect: boolean) => {
  if (!user) {
    return;
  }
  // Every answered question passes through here; the starting page's conversion funnel counts it as question use.
  recordStudyAction("questions", undefined, user);

  const streakUpdate = fetch(
    `${JUNGLE_BACKEND_DOMAIN_FOR_ROUTES_MIGRATED_TO_CLOUDFLARE_WORKER}/streaks/update_streak_after_answering_a_question`,
    {
      method: "POST",
      body: JSON.stringify({
        user: user,
        user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, // Returns user timezone in strings like "America/New_York"
        is_correct: isCorrect,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    }
  )
    .then(async (response) => {
      return response.json();
    })
    .then(async (response) => {
      if (response.status !== "success") {
        console.error("Server returned error status while updating streak", { userId: user?.id });
      }
    })
    .catch((error) => {
      console.error("Network/API error while updating streak", {
        userId: user?.id,
        errorMessage: error.message,
      });
    });
  streakUpdatesInFlight.add(streakUpdate);
  try {
    await streakUpdate;
  } finally {
    streakUpdatesInFlight.delete(streakUpdate);
  }
};

/** Resolves once every answer sent so far has been counted by the Worker (or failed), or after the wait */
export async function waitForStreakUpdatesInFlight(maximumWaitMilliseconds = 4_000): Promise<void> {
  if (streakUpdatesInFlight.size === 0) return;
  let timeoutId: ReturnType<typeof setTimeout>;
  try {
    await Promise.race([
      Promise.allSettled([...streakUpdatesInFlight]),
      new Promise((resolve) => (timeoutId = setTimeout(resolve, maximumWaitMilliseconds))),
    ]);
  } finally {
    clearTimeout(timeoutId);
  }
}

export type FirstReviewStreakDayOutcome = "recorded" | "already_today" | "failed";

type RecordFirstReviewDayResponse = {
  status?: string;
  streak_day_recorded?: boolean;
  already_had_today?: boolean;
  current_streak_count?: number;
};

/**
 * The first review, at whatever length, earns today's streak day (owner, 2026-10-09: "Have people achieve their day
 * 1 streak after they finish the mini review (no matter what size the mini review is)").
 *
 * The streak is the Worker's: POST /streaks/record_first_review_day (Firebase ID token; the Worker checks it is the
 * learner's own) records today's day the way its 10th-answer trigger does, marks the day's goal as met (so a later
 * answer cannot add the day again) and leaves `should_show_streak_increased_page` false, so the first review's own
 * streak page is the only one. It answers whether it recorded the day or the streak already had today. The Worker
 * must be deployed before this frontend: an older Worker answers 404, which counts as a failure (the first review then
 * earns no day, and its streak page is left out: pagesAfterTheFirstReview.ts).
 */
export async function recordTheFirstReviewAsTodaysStreakDay(
  userId: string | null | undefined
): Promise<FirstReviewStreakDayOutcome> {
  if (!userId) return "failed";
  // The answers' own streak updates go first, so the Worker counts the day after them
  await waitForStreakUpdatesInFlight(FIRST_REVIEW_ANSWER_SETTLEMENT_WAIT_MILLISECONDS);
  try {
    const authorizationHeaders = await getFirebaseAuthorizationHeaders();
    const response = await fetch(
      `${JUNGLE_BACKEND_DOMAIN_FOR_ROUTES_MIGRATED_TO_CLOUDFLARE_WORKER}/streaks/record_first_review_day`,
      {
        method: "POST",
        headers: { ...authorizationHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
      }
    );
    if (!response.ok) {
      trackError(new Error(`record_first_review_day answered ${response.status}`), {
        scope: "first-review-streak-day",
        userId,
        // A 404 is a Worker from before the route (the Worker deploys first, so only briefly)
        level: response.status === 404 ? "warning" : "error",
        extra: { httpStatus: response.status },
      });
      return "failed";
    }
    const result = (await response.json()) as RecordFirstReviewDayResponse;
    if (result.streak_day_recorded) return "recorded";
    if (result.already_had_today) return "already_today";
    trackError(new Error("record_first_review_day answered without an outcome"), {
      scope: "first-review-streak-day",
      userId,
      extra: { responseStatus: result.status ?? null },
    });
    return "failed";
  } catch (error) {
    // A network error: the learner may be offline
    trackError(error, { scope: "first-review-streak-day", userId, level: "warning" });
    return "failed";
  }
}
